import React, { useEffect, useState } from "react";
import {
  Box,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { useSearchParams } from "react-router-dom";
import "../../styles/propertyInformation.css";
import GooglePlacesAutocomplete, {
  Place,
} from "../../subcomponents/GoogleAutocomplete";
import {
  validateEmail,
  validatePhoneNumber,
  validationMessages,
} from "../../actions/validations";
import {
  converttoDayjsTime,
  converttoStandardTime,
  propertyTime,
} from "../../../constants/PropertyInformation";
import { TutorialComponent } from "../../Tour/DemoTour";
import { RoomPropertyEnum } from "../../../constants/NavbarList";
import { getProductType, ProductEnum, SearchParams } from "../../../constants/login";

const SectionHeader = ({
  icon,
  title,
}: {
  icon: React.ReactNode;
  title: string;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    className="property-information-section-header"
  >
    <Box className="property-information-section-header-icon">{icon}</Box>
    <Typography
      variant="subtitle1"
      className="property-information-section-header-title"
    >
      {title}
    </Typography>
  </Stack>
);

const PropertyInformation = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [searchParams] = useSearchParams();
  const productType = getProductType(
    Number(searchParams.get(SearchParams.PRODUCT)),
  );
  const isSpaLogin = productType === ProductEnum.SPA;
  const isGolfLogin = productType === ProductEnum.GOLF;
  const openTimeLabel = isSpaLogin
    ? "Spa Open Time"
    : isGolfLogin
      ? "Opening Time"
      : "Check-in Time";
  const closeTimeLabel = isSpaLogin
    ? "Spa Close Time"
    : isGolfLogin
      ? "Closing Time"
      : "Check-out Time";
  const [propertyName, setPropertyName] = useState<string | null>(null);
  const [nameError, setNameError] = useState<boolean>(false);
  const [addressLine1, setAddressLine1] = useState<string | null>(null);
  const [addressLine2, setAddressLine2] = useState<string | null>(null);
  const [city, setCity] = useState<string | null>(null);
  const [state, setState] = useState<string | null>(null);
  const [country, setCountry] = useState<string | null>(null);
  const [zipCode, setZipCode] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [phoneNumberError, setPhoneNumberError] = useState<boolean>(false);
  const [email, setEmail] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [checkinTime, setCheckinTime] = useState<any>(propertyTime.CHECK_IN);
  const [checkoutTime, setCheckoutTime] = useState<any>(propertyTime.CHECK_OUT);
  const [nameEntered, setNameEntered] = useState<boolean>(false);
  const isTourCompleted =
    localStorage.getItem("isTourEnabled") === "true" ||
    !localStorage.getItem("isTourEnabled");

  useEffect(() => {
    validateFields();
    data({
      propertyName,
      addressLine1,
      addressLine2,
      city,
      state,
      country,
      zipCode,
      phoneNumber,
      email,
      checkinTime: converttoStandardTime(checkinTime),
      checkoutTime: converttoStandardTime(checkoutTime),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    propertyName,
    addressLine1,
    addressLine2,
    city,
    state,
    country,
    zipCode,
    phoneNumber,
    email,
    checkinTime,
    checkoutTime,
  ]);

  const validateFields = () => {
    setNameError(false);
    setEmailError(false);
    setPhoneNumberError(false);
    if (email && !validateEmail(email)) {
      setEmailError(true);
    }
    if (phoneNumber && !validatePhoneNumber(phoneNumber)) {
      setPhoneNumberError(true);
    }
    if (propertyName === "" && nameEntered) {
      setNameError(true);
    }
  };

  useEffect(() => {
    if (fieldValues && fieldValues.propertyName !== null) {
      setPropertyName(fieldValues.propertyName);
      setAddressLine1(fieldValues.addressLine1);
      setAddressLine2(fieldValues.addressLine2);
      setCity(fieldValues.city);
      setState(fieldValues.state);
      setCountry(fieldValues.country);
      setPhoneNumber(fieldValues.phoneNumber);
      setEmail(fieldValues.email);
      setZipCode(fieldValues.zipCode);
      setCheckinTime(converttoDayjsTime(fieldValues.checkinTime));
      setCheckoutTime(converttoDayjsTime(fieldValues.checkoutTime));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateAddress = (addressComponents: any) => {
    let nextAddressLine1 = "";
    let nextAddressLine2 = "";
    let nextCity = "";
    let nextState = "";
    let nextCountry = "";
    let nextZipCode = "";

    addressComponents.forEach((component: any) => {
      const primaryType = component.types[0];

      switch (primaryType) {
        case "street_number":
          nextAddressLine1 = component.long_name + " " + nextAddressLine1;
          break;
        case "route":
          nextAddressLine1 = nextAddressLine1 + component.long_name;
          break;
        case "sublocality":
        case "sublocality_level_1":
        case "neighborhood":
        case "subpremise":
          nextAddressLine2 = component.long_name;
          break;
        case "locality":
          nextCity = component.long_name;
          break;
        case "administrative_area_level_1":
          nextState = component.long_name;
          break;
        case "country":
          nextCountry = component.long_name;
          break;
        case "postal_code":
          nextZipCode = component.long_name;
          break;
      }
    });

    setAddressLine1(nextAddressLine1.trim());
    if (nextAddressLine2) setAddressLine2(nextAddressLine2.trim());
    if (nextCity) setCity(nextCity);
    if (nextState) setState(nextState);
    if (nextCountry) setCountry(nextCountry);
    if (nextZipCode) setZipCode(nextZipCode);
  };

  const tfClass = "property-information-textfield";

  return (
    <Stack
      direction="column"
      width="100%"
      className="property-information-root"
    >
      <Paper elevation={0} className="property-information-card">
        {/* Basic Information */}
        <Box>
          <SectionHeader
            icon={<BusinessOutlinedIcon fontSize="small" />}
            title="Basic Information"
          />
          <Stack className="property-information-stack-gap">
            <TextField
              id="propertyName"
              variant="outlined"
              label="Property Name"
              fullWidth
              value={propertyName ?? ""}
              error={nameError}
              helperText={nameError ? validationMessages.propertyName : ""}
              required
              onChange={(e: any) => {
                setPropertyName(e.target.value);
                setNameEntered(true);
              }}
              className={tfClass}
            />
            <Box className="property-information-search-wrap">
              <GooglePlacesAutocomplete
                apiKey={process.env.REACT_APP_GOOGLE_API_KEY ?? ""}
                placeholder="Search for the address"
                onPlaceSelected={(place: Place) => {
                  updateAddress(place.addressComponents);
                }}
              />
              <SearchOutlinedIcon
                className="property-information-search-icon"
                fontSize="small"
              />
            </Box>
          </Stack>
        </Box>

        <Box className="property-information-section-divider" />

        {/* Address Details */}
        <Box>
          <SectionHeader
            icon={<PlaceOutlinedIcon fontSize="small" />}
            title="Address Details"
          />
          <Box className="property-information-field-grid">
            <TextField
              id="addressLine1"
              variant="outlined"
              label="Street Address"
              fullWidth
              value={addressLine1 ?? ""}
              onChange={(e: any) => setAddressLine1(e.target.value)}
              className={tfClass}
            />
            <TextField
              id="addressLine2"
              variant="outlined"
              label="Suite/Unit"
              fullWidth
              value={addressLine2 ?? ""}
              onChange={(e: any) => setAddressLine2(e.target.value)}
              className={tfClass}
            />
            <TextField
              id="city"
              variant="outlined"
              label="City"
              fullWidth
              value={city ?? ""}
              onChange={(e: any) => setCity(e.target.value)}
              className={tfClass}
            />
            <TextField
              id="state"
              variant="outlined"
              label="State/Province"
              fullWidth
              value={state ?? ""}
              onChange={(e: any) => setState(e.target.value)}
              className={tfClass}
            />
            <TextField
              id="country"
              variant="outlined"
              label="Country"
              fullWidth
              value={country ?? ""}
              onChange={(e: any) => setCountry(e.target.value)}
              className={tfClass}
            />
            <TextField
              id="zipcode"
              variant="outlined"
              label="ZIP/Postal Code"
              fullWidth
              value={zipCode ?? ""}
              onChange={(e: any) => setZipCode(e.target.value)}
              className={tfClass}
            />
          </Box>
        </Box>

        <Box className="property-information-section-divider" />

        {/* Contact Information */}
        <Box>
          <SectionHeader
            icon={<ContactsOutlinedIcon fontSize="small" />}
            title="Contact Information"
          />
          <Box className="property-information-field-grid">
            <TextField
              id="phoneNumber"
              variant="outlined"
              label="Phone Number"
              fullWidth
              type="tel"
              value={phoneNumber ?? ""}
              error={phoneNumberError}
              helperText={
                phoneNumberError ? validationMessages.phoneNumber : ""
              }
              onChange={(e: any) =>
                setPhoneNumber(e.target.value >= 0 ? e.target.value : null)
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <PhoneOutlinedIcon
                      fontSize="small"
                      className="property-information-adornment-icon"
                    />
                  </InputAdornment>
                ),
              }}
              className={tfClass}
            />
            <TextField
              id="email"
              variant="outlined"
              label="Email Address"
              fullWidth
              value={email ?? ""}
              error={emailError}
              helperText={emailError ? validationMessages.email : ""}
              onChange={(e: any) => setEmail(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <EmailOutlinedIcon
                      fontSize="small"
                      className="property-information-adornment-icon"
                    />
                  </InputAdornment>
                ),
              }}
              className={tfClass}
            />
          </Box>
        </Box>

        <Box className="property-information-section-divider" />

        {/* Operating Hours */}
        <Box>
          <SectionHeader
            icon={<AccessTimeOutlinedIcon fontSize="small" />}
            title="Operating Hours"
          />
          <Box className="property-information-field-grid">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                label={openTimeLabel}
                value={checkinTime}
                onChange={(newValue) => setCheckinTime(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    className: tfClass,
                  },
                }}
              />
              <TimePicker
                label={closeTimeLabel}
                value={checkoutTime}
                onChange={(newValue) => setCheckoutTime(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    className: tfClass,
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>
      </Paper>
      {!isTourCompleted && (
        <TutorialComponent page={RoomPropertyEnum.propertyInformation} />
      )}
    </Stack>
  );
};

export default PropertyInformation;
